import { LayerHost } from "@fluentui/react";
import { css, DefaultButton, IconButton, IPanelHeaderRenderer, IPanelProps, IRenderFunction, Label, MessageBar, MessageBarType, Panel, PanelType, Persona, PrimaryButton, Spinner, Stack, TextField, Toggle, TooltipHost } from "office-ui-fabric-react";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useHistory } from "react-router";
import { getBookingsForLayoutAndTime, saveNewBooking } from "../../../api/bookings";
import { loadLatestPublishedFloorplan } from "../../../api/floorplans";
import { getUserProfile } from "../../../api/userInfo";
import { IBooking } from "../../../types/Booking.types";
import Doors from "../../drawing/Doors";
import { IDesk, IRoomRectangle, IStampable, ISvgCurve, SvgShapeComponent } from "../../../types/Floorplan.types";
import { IRoomEditorDrawingState } from "../../drawing/editor/FloorplanEditor";
import { IPublishedFloorplanLayout } from "../../../types/SiteLocation.types";
import { createDeskSvg } from "../../drawing/SvgElements";
import TextElements from "../../drawing/TextElements";
import { IUserWithPhoto } from "../../../types/User.types";
import ZoomControls from "../../drawing/ZoomControls";
import { ProfileContext } from "../../../Home";
import { constant } from "lodash";
import { Tag } from "../../../types/Tagging.types";

import styles from "./DeskBookingForm.module.scss";
import FloorRects from "../../drawing/FloorRects";
import SvgShapes from "../../drawing/CustomShapes";
import RoomRects from "../../drawing/RoomRects";

export interface IBookingFormProps {
    onDismiss: () => void;
    startDateStr: string;
    endDateStr: string;
    siteId?: number;
    locationId: number;
    floorplanId: string;
    booking?: IBooking;
    readOnly?: boolean;
    postCode?: string;
}

interface ISVGDimensions {
    width: number; height: number; x: number; y: number;
}

const calculateCurvePoints = (curve: ISvgCurve, arg1: SvgShapeComponent, shapeOriginX: number, shapeOriginY: number): [any, any] => {
    // Given points: start, control, and end
    const x0 = (arg1 as any).x + shapeOriginX, y0 = (arg1 as any).y + shapeOriginY; // Start point
    const x1 = curve.x1 + shapeOriginX, y1 = curve.y1 + shapeOriginY; // Control point
    const x2 = curve.x + shapeOriginX, y2 = curve.y + shapeOriginY; // End point

    // Compute tx and ty
    const denom_x = (x0 - 2 * x1 + x2);
    const denom_y = (y0 - 2 * y1 + y2);
    const tx = denom_x !== 0 ? (x0 - x1) / denom_x : null;
    const ty = denom_y !== 0 ? (y0 - y1) / denom_y : null;

    // Parametric functions for x and y
    function bezier_x(t: number): number {
        return (1 - t) ** 2 * x0 + 2 * (1 - t) * t * x1 + t ** 2 * x2;
    }

    function bezier_y(t: number): number {
        return (1 - t) ** 2 * y0 + 2 * (1 - t) * t * y1 + t ** 2 * y2;
    }

    // Evaluate critical points and endpoints
    const x_values: number[] = [bezier_x(0), bezier_x(1)];
    const y_values: number[] = [bezier_y(0), bezier_y(1)];

    if (tx !== null && tx >= 0 && tx <= 1) {
        x_values.push(bezier_x(tx));
    }
    if (ty !== null && ty >= 0 && ty <= 1) {
        y_values.push(bezier_y(ty));
    }

    return [x_values, y_values];
}

const calculateSvgDimensions = (drawing: IRoomEditorDrawingState) => {
    let leftMostPoint = Number.MAX_SAFE_INTEGER, rightMostPoint = 0, topMostPoint = Number.MAX_SAFE_INTEGER, bottomMostPoint = 0;
    [drawing.desks, drawing.doors, drawing.text].forEach((stampables: IStampable[]) => {
        const minX = Math.min(...stampables.map((s) => s.x));
        const maxX = Math.max(...stampables.map((s) => s.x));
        const minY = Math.min(...stampables.map((s) => s.y));
        const maxY = Math.max(...stampables.map((s) => s.y));
        if (minX < leftMostPoint) leftMostPoint = minX;
        if (maxX > rightMostPoint) rightMostPoint = maxX;
        if (minY < topMostPoint) topMostPoint = minY;
        if (maxY > bottomMostPoint) bottomMostPoint = maxY;
    });
    [drawing.roomRectangles, drawing.floorplanRectangles].forEach((rectangles: IRoomRectangle[]) => {
        const minX = Math.min(...rectangles.map((s) => s.x));
        const maxX = Math.max(...rectangles.map((s) => s.x + s.width));
        const minY = Math.min(...rectangles.map((s) => s.y));
        const maxY = Math.max(...rectangles.map((s) => s.y + s.height));
        if (minX < leftMostPoint) leftMostPoint = minX;
        if (maxX > rightMostPoint) rightMostPoint = maxX;
        if (minY < topMostPoint) topMostPoint = minY;
        if (maxY > bottomMostPoint) bottomMostPoint = maxY;
    });
    drawing.floorplanShapes.forEach((shape) => {
        const fallback = Number.MAX_SAFE_INTEGER;
        let [ minX, minY, maxX, maxY ] = shape.path.reduce((prev, cur, idx, arr) => {
            let minx = (cur as any).x + shape.x || fallback;
            let miny = (cur as any).y + shape.y || fallback;
            let maxx = (cur as any).x + shape.x || 0;
            let maxy = (cur as any).y + shape.y || 0;
            if (cur.type === "Curve") {
                const curve = cur as ISvgCurve;
                const [ xVals, yVals ] = calculateCurvePoints(curve, arr[idx - 1], shape.x, shape.y);
                const curveMin = [ Math.min(...xVals), Math.min(...yVals) ];
                const curveMax = [ Math.max(...xVals), Math.max(...yVals) ];
                if (curveMin[0] < minx) minx = curveMin[0];
                if (curveMin[1] < miny) miny = curveMin[1];
                if (curveMax[0] > maxx) maxx = curveMax[0];
                if (curveMax[1] > maxy) maxy = curveMax[1];
            }
            return [ Math.min(prev[0], minx), Math.min(prev[1], miny), Math.max(prev[2], maxx), Math.max(prev[3], maxy) ];
        }, [fallback, fallback, 0, 0]);
        if (minX < leftMostPoint) leftMostPoint = minX;
        if (maxX > rightMostPoint) rightMostPoint = maxX;
        if (minY < topMostPoint) topMostPoint = minY;
        if (maxY > bottomMostPoint) bottomMostPoint = maxY;
    });
    // padding
    leftMostPoint -= 10; rightMostPoint += 10;
    topMostPoint -= 10; bottomMostPoint += 10;
    // constraint
    leftMostPoint = Math.max(0, leftMostPoint);
    rightMostPoint = Math.min(20000, rightMostPoint);
    topMostPoint = Math.max(0, topMostPoint);
    bottomMostPoint = Math.min(20000, bottomMostPoint);
    return {
        width: rightMostPoint - leftMostPoint,
        height: bottomMostPoint - topMostPoint,
        x: leftMostPoint, y: topMostPoint
    } as ISVGDimensions;
};

interface IDrawingProps {
    disableInteraction: boolean;
    layoutDrawing: IRoomEditorDrawingState;
    bookings: IBooking[];
    bookingFormValues: IBooking;
    handleHover: (clientX: number, clientY: number) => void;
    handleInteraction: (disabled: boolean, clientX: number, clientY: number, isTouch?: boolean) => void;
    svgDimensions: ISVGDimensions;
    zoom: number;
    filterTags?: number[];
}

const svgCss = `
.nonAvailableDesk .notAvailableIcon, .nonAvailableDesk .availability {
    display: none;
}
.freeDesk .notAvailableIcon {
    display: none;
}
.selectedDesk .deskRect {
    fill: green;
}
#floorplan {
    filter: url(#addborder);
}
#shapes {
    fill: white;
}
.safari #floorplan {
    filter: none;
}
.safari #floorplan rect {
    stroke: none;
}
`;

const Drawing = (props: IDrawingProps) => {
    const { disableInteraction, filterTags, layoutDrawing, svgDimensions, zoom, handleHover, handleInteraction, bookingFormValues } = props;
    const { desks, doors, roomRectangles, floorplanRectangles, floorplanShapes, text } = layoutDrawing;
    const context = useContext(ProfileContext);
    
    let className: string | undefined = undefined;
    if (/(Macintosh|iPhone|iPad|iPod).*Safari.*/i.test(navigator.userAgent)) className = "safari";

    let width = svgDimensions.width, height = svgDimensions.height;
    const scaleFactor = 1 / zoom;
    if (scaleFactor !== 1) {
        width = svgDimensions.width * scaleFactor;
        height = svgDimensions.height * scaleFactor;
    }
    const viewBox = `${svgDimensions.x} ${svgDimensions.y} ${svgDimensions.width} ${svgDimensions.height}`;

    let filteredDesks = desks;
    if (filterTags !== undefined && filterTags.length) {
        filteredDesks = desks.filter(desk => (desk.tags || []).map(d => d.Id).filter(id => filterTags.includes(id)).length === filterTags.length)
    }

    return (
        <svg  
            id="svgFloorplan"
            version="1.1"
            baseProfile="full"
            className={className}
            width={width} height={height}
            xmlns="http://www.w3.org/2000/svg"
            // onWheel={this._svgHandleWheel}
            // viewBox={`${svgDimensions.x} ${svgDimensions.y} ${svgDimensions.width} ${svgDimensions.height}`}
            onMouseMove={(e) => handleHover(e.clientX, e.clientY)}
            onMouseUp={(e) => handleInteraction(disableInteraction, e.clientX, e.clientY)}
            onTouchStart={(e) => handleInteraction(disableInteraction, e.touches[0].clientX, e.touches[0].clientY, true)}
            viewBox={viewBox}
            >
                <style>{ svgCss }</style>
                    
                <defs id="commonDefs">

                    { /* Mask for red x on non-available desks */ }
                    <clipPath id="nonAvailability">
                        <ellipse ry="7" rx="7" cy="10" cx="10" style={{stroke: "none"}} />
                    </clipPath>

                    { /* Thick floorplan outline */ }
                    <filter id="addborder">
                        <feMorphology in="SourceAlpha" operator="dilate" radius="4" result="thick" />
                        <feFlood floodColor="#999" floodOpacity="1" result="color" />
                        <feComposite in="color" in2="thick" operator="in" result="ouline"/>
                        <feMerge>
                            <feMergeNode in="outline" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                    <g id="floorplanRects">
                        <FloorRects floorplanRectangles={floorplanRectangles}>
                            <SvgShapes committedShapes={floorplanShapes}/>
                        </FloorRects>
                        {/* { floorplanRectangles && floorplanRectangles.length > 0 && floorplanRectangles.map((room,idx) => (
                            <rect key={`fp_${idx}`} data-idx={idx} x={room.x} y={room.y} width={room.width} height={room.height} stroke="none" fill="white" />                                        
                        ))} */}
                    </g>

                    { /* Door template */}
                    <g id="doorTemplate">
                        <path d="m1,18l10,-11c0,0.08 6,1.5 6.8,11z" fill="#fff" stroke="#e3e3e3" strokeDasharray="29" strokeLinecap="round" strokeLinejoin="round" />
                        <line x1="1" y1="18" x2="11" y2="7" stroke="#000" />
                    </g>
                </defs>

            { /* Background */ }
            <rect id="bgColor" width={svgDimensions.width + svgDimensions.x} height={svgDimensions.height + svgDimensions.y} fill="#e3e3e3" />

            { /* Floorplan layout */ }
            <use id="floorplan" xlinkHref="#floorplanRects" />
            <g id="internalRooms">
            <RoomRects roomRectangles={roomRectangles} />
                {/* { roomRectangles && roomRectangles.length > 0 && roomRectangles.map((room,idx) => (
                    <rect key={`ir_${idx}`} data-idx={idx} x={room.x} y={room.y} width={room.width} height={room.height} stroke="black" fill="white" />                                        
                ))} */}
            </g>

            { /* Doorways */ }
            <Doors doors={doors} />

            { /* Text Labels */ }
            <TextElements textBoxes={text} />

            { /* Desks */ }
            <g id="desks">
                { filteredDesks.map((desk, idx) => {
                    const svg = createDeskSvg();
                    const html = svg.innerHTML;
                    let cursor: string | undefined = undefined, transform: string | undefined = undefined;
                    let className = "desk desk-" + idx + "-" + desk.id;
                    
                    if (desk.rotation != null && desk.rotation !== 0) {
                        transform = `rotate(${desk.rotation}, 10, 12)`;
                    }
                    const today = new Date();
                    const startDate = new Date(props.bookingFormValues.Start);
                    const diff = Math.abs(startDate.getTime() - today.getTime()) / 3600000;

                    let permitted = true;
                    if (diff > 48 && desk.bookable && desk.assignedTeams && desk.assignedTeams.length && context && context?.Profile?.Teams) {
                        permitted = false;
                        const userTeamIds = context?.Profile?.Teams?.map((t) => t.Id);
                        const deskTeams = desk.assignedTeams;
                        if (userTeamIds && deskTeams.some((val, idx, arr) => userTeamIds.indexOf(val) >= 0)) {
                            permitted = true;
                        }
                    }
                    if (!desk.bookable || !permitted) className += " nonAvailableDesk";

                    if (desk.id && props.bookings.filter((b) => b.DeskId === desk.id).length === 0) {
                        className += " freeDesk";
                    }
                    if (bookingFormValues && bookingFormValues.DeskId === desk.id) {
                        className += " selectedDesk";
                    }
                    
                    return (
                        <svg key={`desk_${idx}_${desk.id}`} data-idx={idx + "_" + desk.id} className={className} pointerEvents="all" style={{cursor}} x={desk.x} y={desk.y} overflow="visible">
                            <g transform={transform} dangerouslySetInnerHTML={{__html: html}}></g>
                        </svg>
                    );
                }) }
            </g>
        </svg>
    );
};

const getDeskFromPoint = (x: number, y: number) => {
    let deskEl: SVGSVGElement|undefined;
    const childEls = document.elementsFromPoint(x, y).filter(el => el.classList.contains("desk") || el.classList.contains("availability"));
    if (childEls.length) {
        const g = childEls[0].parentNode as SVGGElement;
        if (g) {
            deskEl = g.parentNode as SVGSVGElement;
        }
    }
    return deskEl
};

interface IDeskCalloutProps {
    bookings?: IBooking[];
    hoveredBooking?: { booking: IBooking, htmlId: string, deskId: number, targetEl: SVGSVGElement };
    desks?: IDesk[];
    onDismiss: () => void;
}

const DESK_BOOKING_PANEL_Z_INDEX = 3000000;
const DESK_CALLOUT_Z_INDEX = 4000000;
const DESK_CALLOUT_MAX_WIDTH = 260;
const DESK_CALLOUT_GAP = 8;

const DeskCallout = (props: IDeskCalloutProps) => {
    const { hoveredBooking, desks, onDismiss } = props;
    const [ loading, setLoading ] = useState(true);
    const [ user, setUser ] = useState<IUserWithPhoto|undefined>();
    const calloutRootRef = React.useRef<HTMLDivElement | null>(null);

    const dismissCallout = () => {
        onDismiss();
    };

    useEffect(() => {
        const handleDocumentPointerDown = (event: MouseEvent) => {
            const target = event.target as Node | null;
            if (!target || !calloutRootRef.current?.contains(target)) return;

            event.stopImmediatePropagation();
            event.stopPropagation();

            if ((target as HTMLElement).closest("[aria-label=\"Close desk details\"]")) {
                dismissCallout();
            }
        };

        document.addEventListener("mousedown", handleDocumentPointerDown, true);
        return () => document.removeEventListener("mousedown", handleDocumentPointerDown, true);
    }, []);

    const loadUserInfo = async (id: number) => {
        const response = await getUserProfile(id);
        if (response.ok && response.status === 200) {
            const userData = await response.json() as IUserWithPhoto;
            setUser(userData);
        } else {
            setUser(undefined);
        }
    };

    useEffect(() => {
        if (hoveredBooking && hoveredBooking.booking !== undefined) {
            if (!user || user.Id !== hoveredBooking.booking.BookedByUserId) {
                setLoading(true);
                loadUserInfo(hoveredBooking.booking.BookedByUserId);
            } else {
                if (loading) setLoading(false);
            }
        } else {
            if (user !== undefined) {
                setUser(undefined);
            }
            if (loading === true) {
                setLoading(false);
            }
        }
    }, [hoveredBooking, user, loading]);

    if (!hoveredBooking) return null;

    const { booking } = hoveredBooking;
    const desk = desks?.find(d => d.id === hoveredBooking.deskId);

    if (!desk) return null;

    let start, end, onBehalfOf;
    if (booking !== undefined) {
        const startDt = new Date(booking.Start);
        start = startDt.toLocaleTimeString().substr(0,5);
        const endDt = new Date(booking.Finish);
        end = endDt.toLocaleTimeString().substr(0,5);
        onBehalfOf = booking.OnBehalfOfEmail;
    } 

    const targetRect = hoveredBooking.targetEl.getBoundingClientRect();
    const preferredLeft = targetRect.right + DESK_CALLOUT_GAP;
    const showOnLeft = preferredLeft + DESK_CALLOUT_MAX_WIDTH > window.innerWidth;
    const left = showOnLeft ? undefined : preferredLeft;
    const right = showOnLeft ? window.innerWidth - targetRect.left + DESK_CALLOUT_GAP : undefined;
    const top = Math.min(
        Math.max(DESK_CALLOUT_GAP, targetRect.bottom + DESK_CALLOUT_GAP),
        window.innerHeight - DESK_CALLOUT_GAP
    );

    return createPortal(<div
                role="dialog"
                ref={calloutRootRef}
                onMouseDown={(event) => {
                    event.stopPropagation();
                    event.nativeEvent.stopImmediatePropagation();
                }}
                onClick={(event) => {
                    event.stopPropagation();
                    event.nativeEvent.stopImmediatePropagation();
                }}
                style={{
                    position: "fixed",
                    left,
                    right,
                    top,
                    minWidth: 100,
                    maxWidth: Math.min(DESK_CALLOUT_MAX_WIDTH, window.innerWidth - (DESK_CALLOUT_GAP * 2)),
                    padding: 10,
                    background: "white",
                    border: "1px solid #e1e1e1",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                    zIndex: DESK_CALLOUT_Z_INDEX
                }}
            >
        <div>
        { desk !== undefined && (
            <div style={{display: "flex", justifyContent:"space-between", alignItems:"center"}}>
                <div>{(desk.alias || "Un-named desk (id: " + desk.id + ")")}</div>
                <button
                    type="button"
                    aria-label="Close desk details"
                    onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        event.nativeEvent.stopImmediatePropagation();
                    }}
                    onClick={(event) => {
                        event.stopPropagation();
                        event.nativeEvent.stopImmediatePropagation();
                        dismissCallout();
                    }}
                    style={{
                        border: 0,
                        background: "transparent",
                        color: "#0078d4",
                        cursor: "pointer",
                        fontSize: 22,
                        lineHeight: 1,
                        padding: 4
                    }}
                >
                    X
                </button>
            </div>
        )}
        { loading === true && (
            <Spinner />
        )}
        { user !== undefined && (
            <div>
                <strong>Booked by:</strong>
                <div>
                    <Persona imageUrl={user.Photo ? `data:${user.Photo.Type};base64,${user.Photo.Data}` : undefined} text={user.FullName} secondaryText={user.Email} />
                </div>
                { user.UseTeams === true && (
                    <a href={`https://teams.microsoft.com/l/chat/0/0?users=${user.Email}`} target="_blank" rel="noreferrer" style={{display: "flex", justifyContent: "flex-start", alignItems: "center", marginTop: 10, marginBottom: 10}}>
                        <img
                            src="https://static2.sharepointonline.com/files/fabric/assets/brand-icons/product/svg/teams_48x1.svg"
                            width="24"
                            height="24"
                            alt="Teams product icon"
                            style={{marginRight: 10}}
                        /> Contact on Teams
                    </a>
                )}
                { booking !== undefined && Boolean(onBehalfOf) === true && (
                    <div><strong>Booked on behalf of:</strong> {onBehalfOf}</div>
                )}
            </div>
        )}
        { booking !== undefined && start !== undefined && end !== undefined && (
            <div>
                <strong>Start:</strong> {start}<br/>
                <strong>End:</strong> {end}
            </div>
        )}
        </div>
    </div>, document.body);
}

export const DeskBookingForm = (props: IBookingFormProps) => {
    const panelHeaderRef = React.useRef<HTMLDivElement | null>(null);
    const fallbackHostIdRef = React.useRef(`panel-layerhost-${Math.random().toString(36).slice(2)}`);
    const { floorplanId, onDismiss, readOnly } = props;
    const history = useHistory();
    const context = useContext(ProfileContext);

    const [ loading, setLoading ] = useState(true);
    const [ saving, setSaving ] = useState(false);
    const [ layoutDrawing, setLayoutDrawing ] = useState<IRoomEditorDrawingState>();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [ parkingEnabled, setParkingEnabled ] = useState<boolean|undefined>();
    const [ availableParking, setAvailableParking ] = useState<number|undefined>();
    const [ bookings, setBookings ] = useState<IBooking[]>();
    const [ hoveredBooking, setHoveredBooking ] = useState<{booking: IBooking, htmlId: string, deskId: number, targetEl: SVGSVGElement}>();
    const [ bookingFormValues, setBookingFormValues ] = useState<IBooking>({
        Start: new Date(decodeURIComponent(props.startDateStr)).toISOString(),
        Finish: new Date(decodeURIComponent(props.endDateStr)).toISOString(),
        FloorplanId: props.floorplanId,
        DeskId: props.booking?.DeskId
    } as IBooking);
    const [ locationName, setLocationName ] = React.useState<string>();
    const [ locationBuildingNo, setLocationBuildingNo ] = React.useState<string>();
    const [ siteNotes, setSiteNotes ] = React.useState<string>();
    const [ zoom, setZoom ] = useState(1);
    const [ error, setError ] = useState<string>();
    const [ formValid, setFormValid ] = useState(false);
    const [ tags, setTags ] = useState<Tag[]|undefined>();
    const [ tagFilter, setTagFilter ] = useState<number[]>([]);


    const toggleTag = useCallback((tagId: number) => {
        if (tagFilter.indexOf(tagId) < 0) {
            setTagFilter([...tagFilter, tagId]);
        } else {
            setTagFilter(tagFilter.filter(t => t !== tagId));
        }
    }, [tagFilter]);

    const _load = async () => {
        const response = await loadLatestPublishedFloorplan(floorplanId);
        if (response.ok) {
            const layout = await response.json() as IPublishedFloorplanLayout;
            setLocationName(layout.LocationName);
            setLocationBuildingNo(layout.LocationBuildingNo);
            setSiteNotes(layout.SiteNotes);
            const drawing = JSON.parse(layout.Json) as IRoomEditorDrawingState;
            if (drawing) {
                if (!drawing.floorplanShapes) drawing.floorplanShapes = [];
                const deskTags = drawing.desks.reduce((prev: Tag[], cur: IDesk, idx: number, arr: IDesk[]) => {
                    if (cur.tags && cur.tags.length) {
                        cur.tags.forEach(tag => {
                            if (prev.map(p => p.Id).indexOf(tag.Id) < 0) {
                                prev.push(tag);
                            }
                        });
                    }
                    return prev;
                }, [] as Tag[]);
                setTags(deskTags);
                if (layout.HasParking) setParkingEnabled(true);
                let totalSpaces = layout.ParkingLimit;
                setLayoutDrawing(drawing);
                setBookingFormValues({...bookingFormValues, FloorplanId: props.floorplanId, LayoutId: layout.Id});

                const bookingsResponse = await getBookingsForLayoutAndTime(props.floorplanId, decodeURIComponent(props.startDateStr), decodeURIComponent(props.endDateStr));
                if (bookingsResponse.ok) {
                    const bookings = await bookingsResponse.json() as IBooking[];
                    if (bookings) {
                        let parkingBooked = bookings.filter((b) => b.IncludeParking).length;
                        if (layout.HasParking && totalSpaces > 0) setAvailableParking(totalSpaces - parkingBooked);
                        setBookingFormValues({...bookingFormValues, IncludeParking: layout.HasParking && (totalSpaces - parkingBooked > 0)});
                        setBookings(bookings);
                        setLoading(false);
                    } else {
                        setError("Unable to load bookings for this floorplan and time");
                    }
                } else {
                    setError("An error occured while loading booking information: " + bookingsResponse.statusText);
                }
            } else {
                setError("Unable to load the layout drawing for the selected floorplan");
            }
        } else {
            setError("An error occured while loading the layout drawing: " + response.statusText);
        }
    };

    useEffect(() => {
        if (props.booking) setBookingFormValues({...props.booking})
        _load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setFormValid(bookingFormValues.DeskId !== undefined);
    }, [ bookingFormValues ]);

    if (error !== undefined) {
        return (
            <Panel type={PanelType.medium} isOpen={true} headerText="Error" onDismiss={onDismiss}>
                <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
            </Panel>
        );
    }

    const _save = async () => {
        setSaving(true);
        const booking = { ...bookingFormValues };
        const response = await saveNewBooking("desk", booking);
        if (response.ok && response.status === 200) {
            setSaving(false);
            history.push("/menu");
        } else {
            const responseText = await response.text();
            alert('Unable to complete booking: ' + responseText);
            if (/desk has already been booked/i.test(responseText)) {
                setBookingFormValues({...bookingFormValues, DeskId: undefined as any as number});
                _load();
            }
            setSaving(false);
        }
    };

    let svgDimensions: ISVGDimensions|undefined;
    if (layoutDrawing) svgDimensions = calculateSvgDimensions(layoutDrawing);

    let deskName;
    if (layoutDrawing && bookingFormValues && bookingFormValues.DeskId) {
        const desk = layoutDrawing.desks.filter((d) => d.id === bookingFormValues.DeskId)[0];
        deskName = desk.alias || `Un-named Desk (Id: ${desk.id})`;
    }

    let startDate = "", endDate = "";
    if (bookingFormValues.Start) {
        const dt = new Date(bookingFormValues.Start)
        startDate = dt.toLocaleDateString() + " " + dt.toLocaleTimeString().substr(0,5);
    }
    if (bookingFormValues.Finish) {
        const dt = new Date(bookingFormValues.Finish)
        endDate = dt.toLocaleDateString() + " " + dt.toLocaleTimeString().substr(0,5);
    }

    return (
        <Panel 
            type={PanelType.custom} 
            customWidth="1000px" 
            isOpen={true} 
            layerProps={{ styles: { root: { zIndex: DESK_BOOKING_PANEL_Z_INDEX } } }}
            headerText={readOnly ? "Booking" : "Book a desk"} 
            onDismiss={() => {
                if (hoveredBooking !== undefined) {
                    setHoveredBooking(undefined);
                    return;
                }
                onDismiss();
            }}
            onRenderHeader={(panelProps: IPanelProps | undefined, defaultRender?: IRenderFunction<IPanelProps>) => (
                <div>
                    <Stack styles={{root: { width: "100%" }}}>
                        { defaultRender !== undefined ? defaultRender(panelProps) : null }
                        <Stack horizontal horizontalAlign="start" styles={{root: { margin: "10px 24px" }}} tokens={{childrenGap: 10}}>
                            <div><strong>Location:</strong> { locationName }</div>
                            <div><strong>Where:</strong> { locationBuildingNo }</div>
                        </Stack>
                        { Boolean(siteNotes) === true && (
                            <MessageBar styles={{root: { margin: "0px 24px" }}} messageBarType={MessageBarType.info}>{siteNotes}</MessageBar>
                        )}
                    </Stack>
                </div>
            )}
            onRenderFooter={(panelProps) => {
                let closeLabel = readOnly ? "Close" : "Cancel";
                return <Stack horizontal horizontalAlign="end" style={{padding: 20}}>
                    { !readOnly && (
                        <PrimaryButton disabled={!formValid || saving} iconProps={{iconName: "Save"}} onClick={() => { _save() }} style={{marginRight: 20}}>
                            Save 
                            { saving === true && (
                                <Spinner style={{marginLeft: 10}} />
                            )}
                        </PrimaryButton>
                    )}
                    <DefaultButton iconProps={{iconName: "Cancel"}} text={closeLabel} onClick={onDismiss} />
                </Stack>;
            }}
        >
            { loading === true && (
                <Spinner />
            )}
            { loading === false && props.postCode !== undefined && (
                <DefaultButton 
                    text="Get Directions" 
                    iconProps={{iconName: "MapDirections"}} 
                    style={{margin: "20px 0px"}}
                    href={`https://www.google.com/maps/dir/?api=1&destination=${ props.postCode }`}
                    target="_blank"
                ></DefaultButton>
            )}
            { loading === false && layoutDrawing !== undefined && bookings !== undefined && svgDimensions !== undefined && (
                <div style={{position:"relative"}}>
                    <div onScroll={() => setHoveredBooking(undefined)} style={{width: "calc(100% - 20px)", height: svgDimensions.height + 20, overflow: "auto", border: "1px solid #e3e3e3", padding: 10, maxHeight: "60vh"}}>
                        <Drawing 
                            filterTags={tagFilter}
                            disableInteraction={readOnly === true}
                            layoutDrawing={layoutDrawing} 
                            bookings={bookings} 
                            bookingFormValues={bookingFormValues}
                            svgDimensions={svgDimensions} 
                            zoom={zoom}
                            handleHover={(clientX, clientY) => {
                                const deskEl = getDeskFromPoint(clientX, clientY);
                                if (deskEl) {
                                    const idxAttr = deskEl.getAttribute("data-idx");
                                    if (idxAttr) {
                                        const idxAttrSplit = idxAttr.split('_');
                                        const id = parseInt(idxAttrSplit[idxAttrSplit.length-1], 10);
                                        const desk = layoutDrawing.desks.find(d => d.id === id);
                                        if (desk?.id) {
                                            const booking = bookings.filter((b) => b.DeskId === desk.id)[0];
                                            setHoveredBooking({ booking, htmlId: idxAttr, deskId: desk.id, targetEl: deskEl });
                                        }
                                    }
                                }
                            }}
                            handleInteraction={(disabled, clientX, clientY, isTouch) => {
                                 if (disabled) return;
                                 const deskEl = getDeskFromPoint(clientX, clientY);
                                 if (deskEl) {
                                     const idxAttr = deskEl.getAttribute("data-idx");
                                     const idxAttrSplit = idxAttr?.split('_');
                                     const isFree = deskEl.classList.contains("freeDesk");
                                     if (idxAttr && idxAttrSplit?.length && isFree) {
                                         const id = parseInt(idxAttrSplit[idxAttrSplit.length-1], 10);
                                         const desk = layoutDrawing.desks.find(d => d.id === id);
                                         if (desk?.id) {
                                             if (isTouch && (!hoveredBooking || hoveredBooking.deskId !== id)) {
                                                const booking = bookings.filter((b) => b.DeskId === desk.id)[0];
                                                setHoveredBooking({ booking, htmlId: idxAttr, deskId: desk.id, targetEl: deskEl });
                                             } else {
                                                 setBookingFormValues({
                                                     ...bookingFormValues,
                                                     DeskId: desk.id as number
                                                 });
                                             }
                                         }

                                     }
                                 }
                            }}
                        />
                    </div>
                    <ZoomControls absolutePos={true} currentZoom={zoom} onZoom={(newZoom) => setZoom(newZoom)} min={0.1} max={1} />
                </div>
            )}
            { tags !== undefined && tags.length > 0 && (
                <>
                    <Label>Filter by tag:</Label>
                    <Stack horizontal wrap>
                        { tags?.map(tag => <div className={css(styles.tag, tagFilter.includes(tag.Id) ? styles.selected : undefined)} key={tag.Id} onClick={() => toggleTag(tag.Id)}><TooltipHost content={tag.Tooltip}>{tag.Label}</TooltipHost></div>) }
                    </Stack>
                </>
            )}
            { !readOnly && bookingFormValues.DeskId === undefined && (
                <Label style={{color: "red", marginBottom: 20}}>Pick a desk above</Label>
            )}
            { layoutDrawing && layoutDrawing.desks && bookingFormValues.DeskId !== undefined && (
                <TextField label="Desk" value={deskName} readOnly />
            )}
            { bookingFormValues && bookingFormValues.Start && (
                <TextField label="Start" value={startDate} readOnly />
            )}
            { bookingFormValues && bookingFormValues.Finish && (
                <TextField label="Finish" value={endDate} readOnly />
            )}
            <Toggle
                title={!availableParking ? "No parking is available at this time" : `There are ${availableParking} spaces available at this time`}
                disabled={readOnly || !availableParking} label="Include Parking" onText="Yes" offText="No" 
                checked={bookingFormValues.IncludeParking !== undefined ? bookingFormValues.IncludeParking : Boolean(availableParking)} onChange={(e, checked) => {
                    setBookingFormValues({...bookingFormValues, IncludeParking: checked !== undefined ? checked : false});
                }}
            />
            <TextField readOnly={readOnly} multiline label="Reason (optional)" value={bookingFormValues?.Description || ""} onChange={(e, newValue) => {
                setBookingFormValues({...bookingFormValues, Description: newValue || ""});
            }} />
            <Label>Book on behalf of someone else (optional)</Label>
            <p>To book on behalf of someone else, just enter their email below.</p>
            <TextField readOnly={readOnly} value={bookingFormValues.OnBehalfOfEmail} onChange={(e, newValue) => {
                setBookingFormValues({...bookingFormValues, OnBehalfOfEmail: newValue || ""});
            }} />
            { hoveredBooking !== undefined && (
                <DeskCallout desks={layoutDrawing?.desks} hoveredBooking={hoveredBooking} onDismiss={() => setHoveredBooking(undefined)} />
            )}
        </Panel>
    );
}